import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  HelpCircle, Search, ChevronRight, ThumbsUp, ThumbsDown,
  PlayCircle, BookOpen, ArrowLeft, Loader2, LayoutGrid,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { clientPortalService } from '../../services/clientPortal.service';
import { useTour } from '../../tour/TourProvider';

const HelpCenter = () => {
  const tour = useTour();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null); // slug | null = all
  const [query, setQuery] = useState('');
  const [articles, setArticles] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);

  useEffect(() => {
    clientPortalService.getHelpCategories().then(setCategories).catch(() => {});
  }, []);

  const loadArticles = useCallback(async () => {
    setListLoading(true);
    try {
      const params = {};
      if (activeCategory) params.category = activeCategory;
      if (query.trim()) params.q = query.trim();
      const data = await clientPortalService.getHelpArticles(params);
      setArticles(data);
    } catch (e) {
      setArticles([]);
    } finally {
      setListLoading(false);
    }
  }, [activeCategory, query]);

  useEffect(() => {
    const t = setTimeout(loadArticles, 250);
    return () => clearTimeout(t);
  }, [loadArticles]);

  const openArticle = useCallback(async (slug) => {
    setArticleLoading(true);
    setFeedbackGiven(false);
    try {
      const data = await clientPortalService.getHelpArticle(slug);
      setArticle(data);
      setSearchParams({ article: slug });
    } catch (e) {
      toast.error('Could not open that article.');
    } finally {
      setArticleLoading(false);
    }
  }, [setSearchParams]);

  // Deep-link: ?article=slug
  useEffect(() => {
    const slug = searchParams.get('article');
    if (slug && (!article || article.slug !== slug)) {
      openArticle(slug);
    }
    if (!slug && article) {
      setArticle(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const closeArticle = () => {
    setArticle(null);
    const sp = new URLSearchParams(searchParams);
    sp.delete('article');
    setSearchParams(sp);
  };

  const sendFeedback = async (helpful) => {
    if (!article || feedbackGiven) return;
    try {
      await clientPortalService.submitHelpFeedback(article.id, helpful);
      setFeedbackGiven(true);
      toast.success('Thanks for your feedback!');
    } catch (e) {
      toast.error('Could not record feedback.');
    }
  };

  // ── Article reader ──
  if (article) {
    return (
      <div className="max-w-3xl mx-auto space-y-6" data-testid="help-article-view">
        <Button variant="ghost" onClick={closeArticle} className="text-gray-600 -ml-2" data-testid="help-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Help Center
        </Button>
        <Card>
          <CardContent className="p-6 lg:p-8">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="help-article-title">{article.title}</h1>
            {article.summary && <p className="text-gray-500 mt-2">{article.summary}</p>}
            {articleLoading ? (
              <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : (
              <div
                className="prose prose-sm sm:prose max-w-none mt-6 text-gray-700"
                data-testid="help-article-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.body_html || '') }}
              />
            )}

            {article.related && article.related.length > 0 && (
              <div className="mt-8 pt-6 border-t">
                <p className="text-sm font-semibold text-gray-700 mb-2">Related articles</p>
                <div className="flex flex-wrap gap-2">
                  {article.related.map((r) => (
                    <Button key={r.slug} variant="outline" size="sm" onClick={() => openArticle(r.slug)}>
                      {r.title}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 pt-6 border-t flex items-center gap-3" data-testid="help-feedback">
              <span className="text-sm text-gray-600">Was this helpful?</span>
              <Button
                variant="outline" size="sm" disabled={feedbackGiven}
                onClick={() => sendFeedback(true)} data-testid="help-feedback-yes"
              >
                <ThumbsUp className="h-4 w-4 mr-1" /> Yes
              </Button>
              <Button
                variant="outline" size="sm" disabled={feedbackGiven}
                onClick={() => sendFeedback(false)} data-testid="help-feedback-no"
              >
                <ThumbsDown className="h-4 w-4 mr-1" /> No
              </Button>
              {feedbackGiven && <span className="text-sm text-green-600">Thanks!</span>}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Browse / search ──
  return (
    <div className="space-y-6" data-testid="help-center-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-10 text-white">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-white/15 rounded-full flex items-center justify-center">
            <HelpCircle className="h-6 w-6" />
          </div>
          <h1 className="text-3xl font-bold">Help Center</h1>
        </div>
        <p className="text-amber-50/90 max-w-2xl mb-5">
          Search step-by-step guides for ordering, invoices, payments, documents, your team, and more.
        </p>
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles…"
            className="pl-10 h-12 bg-white text-gray-900 border-0"
            data-testid="help-search-input"
          />
        </div>
        <div className="mt-4">
          <Button
            variant="secondary"
            className="bg-white/15 hover:bg-white/25 text-white border-0"
            onClick={() => tour?.replayTour()}
            data-testid="help-replay-tour-btn"
          >
            <PlayCircle className="h-4 w-4 mr-2" /> Replay the guided tour
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Category rail */}
        <aside className="space-y-1" data-testid="help-category-rail">
          <button
            onClick={() => setActiveCategory(null)}
            data-testid="help-cat-all"
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeCategory === null ? 'bg-amber-100 text-amber-900' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <LayoutGrid className="h-4 w-4" /> All articles
          </button>
          {categories.map((c) => (
            <button
              key={c.slug}
              onClick={() => setActiveCategory(c.slug)}
              data-testid={`help-cat-${c.slug}`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === c.slug ? 'bg-amber-100 text-amber-900' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span className="flex items-center gap-2 text-left"><BookOpen className="h-4 w-4 flex-shrink-0" />{c.name}</span>
              <Badge variant="secondary" className="text-xs">{c.article_count}</Badge>
            </button>
          ))}
        </aside>

        {/* Article list */}
        <div className="space-y-3" data-testid="help-article-list">
          {listLoading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : articles.length === 0 ? (
            <Card><CardContent className="p-10 text-center text-gray-500" data-testid="help-no-results">
              No articles found{query ? ` for "${query}"` : ''}.
            </CardContent></Card>
          ) : (
            articles.map((a) => (
              <Card
                key={a.id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openArticle(a.slug)}
                data-testid={`help-article-card-${a.slug}`}
              >
                <CardContent className="p-4 flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900">{a.title}</p>
                    {a.summary && <p className="text-sm text-gray-500 mt-1">{a.summary}</p>}
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-1" />
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
